/**
 * Dispatch PDF import for the Load Board: reads a broker dispatch PDF in the
 * browser (no AI, no upload until the user confirms) and turns it into load
 * board values. Fixed rules per broker form; anything uncertain is flagged.
 */
import * as pdfjsLib from 'pdfjs-dist';
import { buildLayout, RawTextItem } from './layout';
import { parseDispatchLayout } from './parsers';
import { ImportedLoad, toImportedLoad } from './normalize';

// Same locally-served worker the rest of the app uses (see pdfOrganizer.ts).
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker/pdf.worker.min.js';

export type { ImportedLoad, DispatchDetails } from './normalize';
export type { SplitInfo, SplitStop } from './split';
export { splitLabel, describeStops } from './split';

export interface DispatchFileResult {
  file: File;
  /** null when the PDF isn't a dispatch form we know */
  imported: ImportedLoad | null;
  error?: string;
}

/** First-page text items of a PDF, with positions. */
const readFirstPageItems = async (file: File): Promise<RawTextItem[]> => {
  const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
  try {
    const page = await pdf.getPage(1);
    const content = await page.getTextContent();
    return content.items.filter((item): item is RawTextItem & typeof item => 'str' in item);
  } finally {
    pdf.destroy();
  }
};

/** Reads one dropped file. Never throws; failures come back as `error`. */
export const readDispatchFile = async (file: File): Promise<DispatchFileResult> => {
  try {
    const parsed = parseDispatchLayout(buildLayout(await readFirstPageItems(file)));
    if (!parsed) {
      return { file, imported: null, error: 'Not a Titan or Avfuel dispatch form' };
    }
    return { file, imported: toImportedLoad(parsed) };
  } catch (error) {
    console.error('Error reading dispatch PDF:', error);
    return { file, imported: null, error: 'Could not read this PDF' };
  }
};
